import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ChangeEvent,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { MfaSelectActions, MfaSelectButton, MfaSelectRadioIcon } from "./MfaSelect.styles";
import { Spinner } from "../../components";
import { COGNITO_V3_CHALLENGE, MFA_FACTOR } from "../../consts";
import { useAuthStore } from "../../stores";

const FACTOR_LABELS: Record<string, string> = {
  [MFA_FACTOR.EMAIL_OTP]: "mfa_select_email",
  [MFA_FACTOR.SOFTWARE_TOKEN_MFA]: "mfa_select_totp"
};

export function MfaSelect() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const cognitoChallenge = useAuthStore((state) => state.cognitoChallenge);
  const availableFactors = useAuthStore((state) => state.mfaAvailableFactors);
  const navigateTo = useAuthStore((state) => state.navigateTo);
  const hasSession = useAuthStore((state) => Boolean(state.session));
  const respondToChallenge = useAuthStore((state) => state.respondToChallenge);
  const cancelMfaSignIn = useAuthStore((state) => state.cancelMfaSignIn);
  const isLoading = useAuthStore((state) => state.isLoading);

  const isCognitoSelection =
    cognitoChallenge?.challengeName === COGNITO_V3_CHALLENGE.SELECT_MFA_TYPE;

  const [selectedFactor, setSelectedFactor] = useState<string>(MFA_FACTOR.EMAIL_OTP);

  useEffect(() => {
    if (!isCognitoSelection && !navigateTo && !hasSession) {
      cancelMfaSignIn();
    }
  }, [cancelMfaSignIn, hasSession, isCognitoSelection, navigateTo]);

  useEffect(() => {
    if (availableFactors.length && !availableFactors.some((factor) => factor === selectedFactor)) {
      setSelectedFactor(availableFactors[0]);
    }
  }, [availableFactors, selectedFactor]);

  if (isLoading) {
    return <Spinner />;
  }

  const getFactorDetail = (factor: string) => {
    if (factor === MFA_FACTOR.EMAIL_OTP) {
      return cognitoChallenge?.parameters?.CODE_DELIVERY_DESTINATION ?? "";
    }

    return t("mfa_select_totp_detail");
  };

  return (
    <Stack
      role="form"
      alignItems="center"
      gap={ isMobile ? 3 : 4 }
      maxWidth={ theme.spacing(56) }
      width="100%"
    >
      <Stack alignItems="center" gap={ 2 } width="100%">
        <Typography
          component="h1"
          color={ theme.customColors.textPrimary }
          fontSize={ theme.typography.pxToRem(isMobile ? 20 : 28) }
          fontWeight={ theme.typography.fontWeightBold }
          lineHeight={ 1.2 }
          textAlign="center"
        >
          { t("mfa_select_title") }
        </Typography>
        <Typography
          color={ theme.customColors.textPrimary }
          fontSize={ theme.typography.pxToRem(isMobile ? 14 : 16) }
          lineHeight={ 1.2 }
          textAlign="center"
        >
          { t("mfa_select_description") }
        </Typography>
      </Stack>

      <FormControl fullWidth>
        <RadioGroup
          aria-label={ t("mfa_select_title") }
          name="mfa-factor"
          onChange={ (event: ChangeEvent<HTMLInputElement>) => {
            setSelectedFactor(event.target.value);
          } }
          sx={ { gap: 2 } }
          value={ selectedFactor }
        >
          { availableFactors.map((factor) => {
            const isSelected = selectedFactor === factor;

            return (
              <FormControlLabel
                key={ factor }
                control={
                  <Radio
                    checkedIcon={ <MfaSelectRadioIcon /> }
                    icon={ <MfaSelectRadioIcon /> }
                    sx={ {
                      "&.Mui-focusVisible": {
                        outline: `2px solid ${theme.customColors.borderAccent}`,
                        outlineOffset: theme.spacing(0.5)
                      },
                      flexShrink: 0,
                      padding: 0
                    } }
                  />
                }
                label={
                  <Stack gap={ 0.5 }>
                    <Typography
                      color={ isSelected
                        ? theme.customColors.backgroundPrimaryCta
                        : theme.customColors.textPrimary }
                      fontSize={ theme.typography.pxToRem(16) }
                      fontWeight={ theme.typography.fontWeightBold }
                      lineHeight={ 1.2 }
                    >
                      { t(FACTOR_LABELS[factor]) }
                    </Typography>
                    <Typography
                      color={ theme.customColors.textPrimary }
                      fontSize={ theme.typography.pxToRem(12) }
                      lineHeight={ 1.2 }
                      sx={ { overflowWrap: "anywhere" } }
                    >
                      { getFactorDetail(factor) }
                    </Typography>
                  </Stack>
                }
                sx={ {
                  ".MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
                  alignItems: "center",
                  border: `1px solid ${isSelected
                    ? theme.customColors.borderPrimaryCta
                    : theme.customColors.border}`,
                  borderRadius: 2,
                  boxSizing: "border-box",
                  gap: 1.5,
                  margin: 0,
                  minHeight: theme.spacing(6.75),
                  padding: theme.spacing(0.75, 1.5),
                  width: "100%"
                } }
                value={ factor }
              />
            );
          }) }
        </RadioGroup>
      </FormControl>

      <MfaSelectActions>
        <MfaSelectButton
          onClick={ cancelMfaSignIn }
          type="button"
          variant="outlined"
        >
          { t("back") }
        </MfaSelectButton>
        <MfaSelectButton
          disabled={ !isCognitoSelection || !availableFactors.some((factor) => factor === selectedFactor) || isLoading }
          onClick={ () => {
            respondToChallenge(selectedFactor);
          } }
          type="button"
          variant="contained"
        >
          { t("confirm") }
        </MfaSelectButton>
      </MfaSelectActions>
    </Stack>
  );
}
